import React, { useState } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { ChevronDown, Loader2, Sparkles } from "lucide-react";

const confColor = (c) => {
  const v = (c || "").toLowerCase();
  if (v === "high") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (v === "medium") return "bg-amber-50 text-amber-600 border-amber-200";
  return "bg-rose-50 text-rose-600 border-rose-200";
};

const shortLevel = (lvl) => {
  if (!lvl) return "";
  const head = lvl.split(/[(/,\-–]| based | with | \u2013 /i)[0].trim();
  return (head || lvl).slice(0, 40);
};

const CareerSnapshot = ({ snapshot, onCreate, generating, disabled, compensation }) => {
  const [expanded, setExpanded] = useState(false);
  const skills = snapshot.primary_skills || [];
  const shown = expanded ? skills : skills.slice(0, 6);
  const level = snapshot.experience_level;
  const inferredRole = snapshot.inferred_target_role;

  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] shadow-xl overflow-hidden h-full" data-testid="career-snapshot-card">
      <div className="flex items-center justify-between bg-sky-50 border-b border-slate-200 px-5 py-3">
        <p className="font-bold text-slate-900">Career Snapshot</p>
        <span className="text-xs font-mono text-slate-500" data-testid="completeness-progress-ring">{snapshot.resume_completeness_pct}% complete</span>
      </div>

      <div className="flex flex-col md:flex-row gap-5 p-5">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-sky-50 border border-sky-200 flex items-center justify-center text-sky-700 font-bold text-lg shrink-0">
              {(snapshot.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-slate-900 break-words sm:truncate">{snapshot.name || "Your Profile"}</h3>
              <p className="text-sm text-slate-500 break-words sm:truncate">{snapshot.current_title || "Title not detected"}</p>
            </div>
            {level && (
              <Badge title={level} className="shrink-0 whitespace-normal text-left sm:ml-auto sm:max-w-[42%] sm:truncate bg-sky-50 text-sky-700 border-sky-200" data-testid="experience-level-badge">
                {shortLevel(level)}
              </Badge>
            )}
          </div>

          {skills.length > 0 && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="snapshot-skills-box">
              <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Primary skills</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {shown.map((s, i) => (
                  <span key={`${s}-${i}`} className="px-2.5 py-1 rounded-md bg-white border border-slate-200 text-xs text-slate-800">{s}</span>
                ))}
                {skills.length > 6 && (
                  <button onClick={() => setExpanded((e) => !e)} className="px-2.5 py-1 rounded-md text-xs text-sky-600 hover:text-sky-700 inline-flex items-center gap-1" data-testid="skills-expand-toggle">
                    {expanded ? "Show less" : `+${skills.length - 6}`}
                    <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>
            </div>
          )}

          {compensation && (compensation.current_ctc || compensation.expected_ctc || compensation.work_model) && (
            <div className="mt-3 grid gap-2.5 sm:grid-cols-3" data-testid="snapshot-compensation">
              {[["Current CTC", compensation.current_ctc && `${compensation.currency} ${compensation.current_ctc}`],
                ["Expected CTC", compensation.expected_ctc && `${compensation.currency} ${compensation.expected_ctc}`],
                ["Working model", [compensation.work_model, compensation.notice_period && `Notice: ${compensation.notice_period}`].filter(Boolean).join(" · ")]]
                .map(([label, value]) => value ? (
                  <div key={label} className="accent-bar rounded-xl border border-slate-200 bg-white p-3 pl-4" style={{ "--accent": "#0D9488" }}>
                    <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{value}</p>
                  </div>
                ) : null)}
            </div>
          )}
        </div>

        <div className="md:w-60 shrink-0 md:border-l md:border-slate-200 md:pl-5 flex flex-col justify-center">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Inferred Target Role</p>
          <p className="text-2xl font-bold text-slate-900 mt-1 leading-tight break-words" data-testid="inferred-role-badge">
            {inferredRole || "Not enough information"}
          </p>
          {snapshot.inferred_target_confidence && (
            <Badge className={`mt-2 w-fit text-[10px] ${confColor(snapshot.inferred_target_confidence)}`}>
              {snapshot.inferred_target_confidence} confidence
            </Badge>
          )}
          {inferredRole && onCreate && (
            <Button
              onClick={() => onCreate(inferredRole)}
              disabled={disabled}
              className="mt-3 w-full bg-sky-700 hover:bg-sky-600 text-white font-semibold"
              data-testid="create-inferred-role-resume-btn"
            >
              {generating ? (
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Generating…</span>
              ) : (
                <span className="flex items-center gap-1.5 text-xs"><Sparkles className="w-4 h-4 shrink-0" /> Modify Your Current Resume</span>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CareerSnapshot;
